<template>
  <div class="captcha-container">
    <div v-if="!provider" class="text-xs text-gray-500">
      CAPTCHA désactivé
    </div>
    <div v-else ref="captchaEl"></div>
  </div>
</template>

<script>
import { defineComponent, onMounted, onBeforeUnmount, ref, watch } from 'vue'

// Ce composant supporte hCaptcha et reCAPTCHA v2 Checkbox
// Configuration via variables d'environnement Vite:
// - VITE_CAPTCHA_PROVIDER = 'hcaptcha' | 'recaptcha'
// - VITE_HCAPTCHA_SITE_KEY
// - VITE_RECAPTCHA_SITE_KEY
export default defineComponent({
  name: 'CaptchaWidget',
  props: {
    modelValue: { type: String, default: '' },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const provider = import.meta.env.VITE_CAPTCHA_PROVIDER?.toLowerCase?.() || ''
    const siteKey = provider === 'hcaptcha'
      ? import.meta.env.VITE_HCAPTCHA_SITE_KEY
      : provider === 'recaptcha'
        ? import.meta.env.VITE_RECAPTCHA_SITE_KEY
        : ''

    const captchaEl = ref(null)
    let widgetId = null

    const reset = () => {
      if (provider === 'hcaptcha' && window.hcaptcha && widgetId !== null) {
        window.hcaptcha.reset(widgetId)
      }
      if (provider === 'recaptcha' && window.grecaptcha && widgetId !== null) {
        window.grecaptcha.reset(widgetId)
      }
      emit('update:modelValue', '')
    }

    const renderCaptcha = () => {
      if (!provider || !siteKey || !captchaEl.value) return

      if (provider === 'hcaptcha') {
        const onLoad = () => {
          widgetId = window.hcaptcha.render(captchaEl.value, {
            sitekey: siteKey,
            callback: (token) => emit('update:modelValue', token),
            'error-callback': () => emit('update:modelValue', ''),
            'expired-callback': () => emit('update:modelValue', ''),
            theme: document.documentElement.classList.contains('dark') ? 'dark' : 'light',
          })
        }
        if (window.hcaptcha) {
          onLoad()
        } else {
          const script = document.createElement('script')
          script.src = 'https://hcaptcha.com/1/api.js?render=explicit'
          script.async = true
          script.defer = true
          script.onload = onLoad
          document.head.appendChild(script)
        }
      }

      if (provider === 'recaptcha') {
        const init = () => {
          widgetId = window.grecaptcha.render(captchaEl.value, {
            sitekey: siteKey,
            callback: (token) => emit('update:modelValue', token),
            'error-callback': () => emit('update:modelValue', ''),
            'expired-callback': () => emit('update:modelValue', ''),
            theme: document.documentElement.classList.contains('dark') ? 'dark' : 'light',
          })
        }
        if (window.grecaptcha && window.grecaptcha.render) {
          init()
        } else {
          const script = document.createElement('script')
          script.src = 'https://www.google.com/recaptcha/api.js?render=explicit'
          script.async = true
          script.defer = true
          script.onload = () => {
            if (window.grecaptcha && window.grecaptcha.render) init()
          }
          document.head.appendChild(script)
        }
      }
    }

    onMounted(renderCaptcha)

    onBeforeUnmount(() => {
      // aucun nettoyage spécial requis, les widgets sont gérés par les libs
    })

    watch(() => document.documentElement.classList.contains('dark'), () => {
      // En cas de changement de thème, réinitialiser pour refléter le thème
      reset()
      renderCaptcha()
    })

    return { provider, captchaEl, reset }
  }
})
</script>

<style scoped>
.captcha-container {
  display: flex;
  justify-content: center;
}
</style>
